import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { Settings, Mail, Bell, Lock, User, Check, Loader2, Key, Copy, Trash2, Plus, AlertTriangle } from 'lucide-react';

function ApiKeySection() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKeyRaw, setNewKeyRaw] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      const data = await api.listApiKeys();
      setKeys(data.keys || []);
    } catch (err) {
      console.error('Load API keys error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const data = await api.createApiKey(newKeyName.trim());
      setNewKeyRaw(data.key);
      setNewKeyName('');
      loadKeys();
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('API Key wirklich widerrufen? Alle Systeme die diesen Key nutzen verlieren den Zugang.')) return;
    try {
      await api.revokeApiKey(keyId);
      setKeys(prev => prev.filter(k => k.id !== keyId));
    } catch (err) {
      alert('Fehler: ' + err.message);
    }
  };

  const handleCopy = () => {
    if (newKeyRaw) {
      navigator.clipboard.writeText(newKeyRaw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
          <Key className="w-4 h-4" /> API Keys
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          API Keys erlauben externen Systemen (z.B. Monitoring) den Zugriff ohne Login.
          Sende den Key als <code className="bg-muted px-1 py-0.5 rounded text-[10px]">X-API-Key</code> Header.
        </p>

        {/* New key reveal */}
        {newKeyRaw && (
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2" data-testid="new-key-reveal">
            <div className="flex items-center gap-2 text-amber-400 text-xs font-medium">
              <AlertTriangle className="w-3.5 h-3.5" />
              Kopiere diesen Key jetzt — er wird nicht mehr angezeigt!
            </div>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={newKeyRaw}
                className="font-mono text-xs bg-background/50"
                data-testid="new-key-value"
              />
              <Button size="sm" variant="outline" onClick={handleCopy} data-testid="copy-key-btn">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setNewKeyRaw(null)}>
              Verstanden, Key gespeichert
            </Button>
          </div>
        )}

        {/* Create new key */}
        <div className="flex items-center gap-2">
          <Input
            placeholder="Key-Name (z.B. Monitoring System)"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            className="text-sm"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            data-testid="new-key-name-input"
          />
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={creating || !newKeyName.trim()}
            data-testid="create-key-btn"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            {creating ? '' : 'Erstellen'}
          </Button>
        </div>

        {/* Key list */}
        {loading ? (
          <div className="text-center py-4 text-muted-foreground text-sm">Lade...</div>
        ) : keys.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-sm">Keine API Keys erstellt</div>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <div
                key={k.id}
                className={`flex items-center justify-between p-2.5 rounded-md border ${k.is_active ? 'border-border' : 'border-red-500/20 bg-red-500/5'}`}
                data-testid={`api-key-${k.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{k.name}</span>
                    {!k.is_active && <Badge variant="destructive" className="text-[10px]">Widerrufen</Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span className="font-mono">{k.key_prefix}...</span>
                    <span>Erstellt: {new Date(k.created_at).toLocaleDateString('de-DE')}</span>
                    {k.last_used_at && (
                      <span>Zuletzt: {new Date(k.last_used_at).toLocaleDateString('de-DE')}</span>
                    )}
                  </div>
                </div>
                {k.is_active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive shrink-0"
                    onClick={() => handleRevoke(k.id)}
                    data-testid={`revoke-key-${k.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [name, setName] = useState('');
  const [reportEnabled, setReportEnabled] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  const loadUser = useCallback(async () => {
    try {
      const data = await api.getMe();
      setUser(data);
      setName(data.name || '');
      setReportEnabled(data.report_enabled || false);
    } catch (err) {
      console.error('Load user error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.updateSettings({ name, report_enabled: reportEnabled });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save settings error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg('');
    try {
      await api.changePassword(currentPassword, newPassword);
      setPasswordMsg('Passwort geändert');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setPasswordMsg('Fehler: ' + (err.message || 'Falsches Passwort'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold">Einstellungen</h1>
        <p className="text-sm text-muted-foreground mt-1">Profil, Benachrichtigungen & API Zugang</p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <User className="w-4 h-4" /> Profil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Email</label>
            <Input value={user?.email || ''} disabled className="bg-muted/30 font-mono text-sm" data-testid="settings-email" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dein Name"
              className="text-sm"
              data-testid="settings-name"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Rolle</label>
            <Badge variant="outline">{user?.role}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* API Keys */}
      <ApiKeySection />

      {/* Notifications */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Bell className="w-4 h-4" /> Benachrichtigungen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Täglicher Statusbericht</p>
              <p className="text-xs text-muted-foreground">Tägliche Zusammenfassung aller Jobs per Email</p>
            </div>
            <Switch
              checked={reportEnabled}
              onCheckedChange={setReportEnabled}
              data-testid="settings-report-toggle"
            />
          </div>
          {reportEnabled && (
            <div className="p-3 rounded-lg bg-muted/30 border border-border">
              <div className="flex items-center gap-2 text-xs">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                <span>Bericht an <span className="font-mono font-semibold">{user?.email}</span></span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Täglich um 08:00 (CET) von noreply@provider.tools
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button
        onClick={handleSaveSettings}
        disabled={saving}
        className="w-full"
        data-testid="settings-save-btn"
      >
        {saving ? (
          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Speichern...</>
        ) : saved ? (
          <><Check className="w-4 h-4 mr-2" /> Gespeichert</>
        ) : (
          <><Settings className="w-4 h-4 mr-2" /> Einstellungen speichern</>
        )}
      </Button>

      {/* Password */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Lock className="w-4 h-4" /> Passwort ändern
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Aktuelles Passwort"
              className="text-sm"
              data-testid="settings-current-pw"
            />
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Neues Passwort"
              className="text-sm"
              data-testid="settings-new-pw"
            />
            <Button
              type="submit"
              variant="outline"
              size="sm"
              disabled={!currentPassword || !newPassword}
              data-testid="settings-change-pw-btn"
            >
              Passwort ändern
            </Button>
            {passwordMsg && (
              <p className={`text-xs ${passwordMsg.includes('Fehler') ? 'text-red-400' : 'text-green-400'}`}>
                {passwordMsg}
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
